import { useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { Header } from './Header';
import { Sidebar } from './Sidebar';
import { CafeWidget } from '../cafe';
import { useKeyboardShortcuts, KeyboardShortcutsModal } from '../common/KeyboardShortcuts';
import { usePreferredLanguage } from '../../hooks/usePreferredLanguage';
import { DataLanguageProvider } from '../../contexts/DataLanguageContext';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { showHelp, setShowHelp } = useKeyboardShortcuts();
  usePreferredLanguage();

  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const handleToggleSidebar = useCallback(() => {
    setSidebarOpen(prev => !prev);
  }, []);

  // Lock background scroll while the mobile drawer is open
  useEffect(() => {
    if (!sidebarOpen) return;
    const main = document.getElementById('main-content');
    const prev = main?.style.overflowY;
    if (main) main.style.overflowY = 'hidden';
    return () => {
      if (main) main.style.overflowY = prev || '';
    };
  }, [sidebarOpen]);

  return (
    <DataLanguageProvider>
      <div className="flex h-dvh overflow-hidden bg-canvas">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={handleCloseSidebar} />

        {/* Main Content Area */}
        <div className="flex flex-col flex-1 w-0 overflow-hidden">
          {/* Header */}
          <Header onMenuClick={handleToggleSidebar} menuOpen={sidebarOpen} />

          {/* Main Content */}
          <main id="main-content" className="flex-1 relative overflow-y-auto focus:outline-none">
            <div className="py-4 sm:py-6 px-4 sm:px-6 lg:px-8 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
              {children}
            </div>
          </main>
        </div>

        {/* Floating Cafe Widget */}
        <CafeWidget />

        {/* Keyboard Shortcuts Help Modal */}
        <KeyboardShortcutsModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
      </div>
    </DataLanguageProvider>
  );
}
